import { useState, useEffect } from 'react';
import { Loader2, Sparkles } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { apiGet, apiPost } from '../../lib/api';
import { TypePicker, FormatToggle, NativeSelect } from './FormControls';

const useOptions = () => {
  const [clients, setClients] = useState([]);
  const [skai, setSkai] = useState([]);
  const [loadingClients, setLoadingClients] = useState(true);
  useEffect(() => {
    apiGet('/api/drive/clients').then((r) => r.json()).then((d) => setClients(d.clients || [])).catch(() => {})
      .finally(() => setLoadingClients(false));
    apiGet('/api/skai/mappings').then((r) => r.json()).then((d) => setSkai(d.mappings || [])).catch(() => {});
  }, []);
  return { clients, skai, loadingClients };
};

export const NewDeliverableModal = ({ deckTypes, onClose, onCreated }) => {
  const { clients, skai, loadingClients } = useOptions();
  const [form, setForm] = useState({
    deliverable_type: 'performance_readout', output_format: 'pptx', title: '', drive_folder_id: '',
    period: '', outline: '', data_summary: '', skai_profile_id: '', skai_days: 30,
  });
  const [submitting, setSubmitting] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const pickType = (t) => setForm((f) => ({ ...f, deliverable_type: t.key, output_format: t.default_format }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.drive_folder_id || !form.period.trim()) {
      toast.error('Title, client and period are required');
      return;
    }
    setSubmitting(true);
    try {
      const res = await apiPost('/api/presentations', {
        ...form,
        title: form.title.trim(), period: form.period.trim(),
        outline: form.outline.trim() || null, data_summary: form.data_summary.trim() || null,
        skai_profile_id: form.skai_profile_id ? Number(form.skai_profile_id) : null,
        skai_days: Number(form.skai_days) || 30,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === 'string' ? data.detail : 'Create failed');
      toast.success('Queued — Claude is building it with the BCM skill');
      onCreated(data.deck);
    } catch (err) {
      toast.error(err.message || 'Create failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="new-deck-modal-backdrop">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="new-deck-modal">
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <div>
            <h2 className="text-xl font-bold text-text-primary">New BCM Deliverable</h2>
            <p className="text-sm text-text-muted mt-1">Pick what to build. Claude applies the BCM brand skill and returns a real file.</p>
          </div>
          <TypePicker types={deckTypes} value={form.deliverable_type} onPick={pickType} />
          <FormatToggle value={form.output_format} onChange={set('output_format')} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <NativeSelect id="new-deck-client" label="Client *" value={form.drive_folder_id} onChange={set('drive_folder_id')}
              placeholder={loadingClients ? 'Loading clients…' : clients.length ? 'Select a client' : 'No client folders available'}
              options={clients.map((c) => ({ value: c.drive_folder_id, label: c.client_name || c.folder_name }))} />
            <div className="space-y-2">
              <Label htmlFor="deck-period">Period *</Label>
              <Input id="deck-period" value={form.period} onChange={set('period')} placeholder="Week of Oct 19, 2026 / Q3 2026" data-testid="new-deck-period" />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="deck-title">Title *</Label>
            <Input id="deck-title" value={form.title} onChange={set('title')} placeholder="Acme Q3 Performance Readout" data-testid="new-deck-title" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="deck-outline">Outline / notes</Label>
            <Textarea id="deck-outline" rows={4} value={form.outline} onChange={set('outline')} data-testid="new-deck-outline"
              placeholder="Talking points, structure, key messages. Claude uses these as the spine." />
          </div>
          <div className="space-y-2">
            <Label htmlFor="deck-data">Data</Label>
            <Textarea id="deck-data" rows={3} value={form.data_summary} onChange={set('data_summary')} data-testid="new-deck-data"
              placeholder="Paste KPIs or tables. Only this data (plus Skai, if attached) is used — nothing is invented." />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
            <div className="md:col-span-2">
              <NativeSelect id="new-deck-skai" label="Attach Skai performance (optional)" value={form.skai_profile_id} onChange={set('skai_profile_id')}
                placeholder="None" options={skai.map((m) => ({ value: String(m.profile_id), label: m.label || `Profile ${m.profile_id}` }))} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="deck-skai-days">Last N days</Label>
              <Input id="deck-skai-days" type="number" min={1} max={365} value={form.skai_days} onChange={set('skai_days')}
                disabled={!form.skai_profile_id} data-testid="new-deck-skai-days" />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t border-border-subtle">
            <Button type="button" variant="outline" onClick={onClose} disabled={submitting} data-testid="new-deck-cancel">Cancel</Button>
            <Button type="submit" disabled={submitting} data-testid="new-deck-submit">
              {submitting ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Sparkles size={14} className="mr-1.5" />}
              Generate
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
